/**
 * Geometry for the word panel that docks beside the passage on a wide screen.
 *
 * A popover anchored under the clicked word covers the lines you were reading. From
 * `WIDE_QUERY` up there is room to put it in the left gutter instead — but not always
 * *enough* room: at 1440 the gutter is about 300px against a 320px panel. So the
 * passage slides right by the shortfall only, which on most screens is a few dozen
 * pixels and on a very wide one is nothing at all.
 */

export const SIDE_PANEL_WIDTH = 320
export const SIDE_PANEL_LEFT = 24

/** Breathing room between the panel and the first letter of the passage. */
const GAP = 32

/** Keeps the passage from being shoved against the right edge of the window. */
const EDGE_PADDING = 16

/** Clears the nav, which the panel scrolls under rather than over. */
export const SIDE_PANEL_TOP_MIN = 88

/**
 * Where the panel's top edge goes: level with the line the word sits on, so the answer
 * appears where the reader was already looking, but never under the nav and never
 * hanging off the bottom. A panel taller than the window starts at the top and scrolls.
 */
export function sidePanelTop(anchor: DOMRect, panelHeight: number, viewportHeight: number): number {
  const lowest = viewportHeight - panelHeight - EDGE_PADDING

  return Math.max(SIDE_PANEL_TOP_MIN, Math.min(anchor.top - 8, lowest))
}

/**
 * How far right the passage must move to clear a panel docked at the left edge.
 * Never negative — a passage that already clears the panel does not move — and never
 * more than the room left on the right, since overlapping is better than overflowing.
 */
export function sidePanelShift(content: DOMRect, viewportWidth: number): number {
  const needed = SIDE_PANEL_LEFT + SIDE_PANEL_WIDTH + GAP - content.left
  const room = viewportWidth - content.right - EDGE_PADDING

  return Math.max(0, Math.min(needed, room))
}
